import { answerQuestions, checkIsSafeCountry, useCountryList } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectArea from "@/components/Select";
import ValidateError from "@/containers/common/ValidateError";
import ContentContainer from "@/containers/common/ContentContainer";
import ConfirmModal from "@/containers/common/ConfirmModal";

interface NationalityProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	ipRegion?: string;
	onNextClick: () => void;
}

interface IFormInput {
	nationality: string;
}

function Nationality(props: NationalityProps) {
	const router = useRouter();
	const { t } = useTranslation("survey");
	const { countryList } = useCountryList({ language: router.locale });
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const {
		control,
		handleSubmit,
		setValue,
		formState: { isValid, errors },
		getValues,
	} = useForm<IFormInput>({
		defaultValues: {
			nationality: "",
		},
	});

	useEffect(() => {
		if (!props.ipRegion) return;
		if (getValues().nationality) return;

		setValue("nationality", props.ipRegion, { shouldValidate: true });
	}, [props.ipRegion]);

	useEffect(() => {
		const countryValue = props.answerFieldToValueMap.get("nationality");
		if (!countryValue) return;
		setValue("nationality", countryValue, { shouldValidate: true });
	}, []);

	const onSubmit: SubmitHandler<IFormInput> = async (data) => {
		if (data.nationality === "CN") {
			setVisible(true);
			return;
		}
		sureSubmit(data);
	};

	const sureSubmit = async (data: IFormInput) => {
		try {
			const countryQuestionId = props.questionFieldToIdMap.get("nationality");
			if (!countryQuestionId) {
				throw Error("missing nationality questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.nationality,
					questionId: countryQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_NATIONALITY_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_NATIONALITY_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			header={
				<SurveyTitle title={t("what-is-your-nationality")} subtitle={t("ensure-it-aligns-with-your-id-documents")} />
			}
			footer={
				<SurveyFooter
					loading={loading}
					disabled={!isValid}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_NATIONALITY_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_NATIONALITY_SECURITY)}
				/>
			}
		>
			<div className="h-12" />
			<Controller
				name="nationality"
				control={control}
				rules={{
					required: t("country-region-cannot-be-empty"),
					validate: {
						checkNationality: async (v) => {
							const isSafeCountry = await checkIsSafeCountry(v);
							return isSafeCountry || t("we-cannot-open-accounts-for-this-country");
						},
					},
				}}
				defaultValue=""
				render={({ field }) => (
					<SelectArea
						{...field}
						placeholder={t("country-region")}
						defaultValue={field.value}
						options={countryList}
						onChange={(value) => {
							field.onChange(value);
							setValue("nationality", value, { shouldValidate: true });
							log2sensors(SensorsEventIds.AO_NATIONALITY_NATIONALITY);
						}}
					/>
				)}
			/>

			<ValidateError error={errors.nationality} />
			<ConfirmModal
				visible={visible}
				onClose={() => setVisible(false)}
				title={t("china-mainland-user-account-opening-notice-title")}
				onCancelText={t("china-mainland-user-account-opening-notice-btn-cancel")}
				onOkText={t("china-mainland-user-account-opening-notice-btn-confirm")}
				onCancel={() => setVisible(false)}
				onOk={() => sureSubmit(getValues())}
			>
				<div className="max-h-80 overflow-y-auto hidden-scroll-bar whitespace-pre-wrap text-left">
					{t("china-mainland-user-account-opening-notice-content")}
				</div>
			</ConfirmModal>
		</ContentContainer>
	);
}

export default Nationality;
